import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Box, useTheme, IconButton } from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWidgetPreferences } from '../../../actions/home/widgetPreferences';
import applyPendingHomeSpaceReorder from '../../../actions/home/applyPendingHomeSpaceReorder';
import { WidgetPreference } from '@core/types/state';
import { THEME_SWITCHING } from '@core/constants/actions';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import HomeWidgetLayout from './layout/HomeWidgetLayout';
import { location as getLocation } from '@app/selectors/routing';

const ThemeToggleFAB = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleToggle = () => {
    const nextTheme = isDarkMode ? 'light' : 'dark';
    localStorage.setItem('theme', nextTheme);
    dispatch({ type: THEME_SWITCHING, theme: nextTheme });
  };

  return (
    <IconButton
      onClick={handleToggle}
      aria-label="Toggle dark/light theme"
      sx={{
        position: 'fixed',
        bottom: '80px',
        right: '24px',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        zIndex: 1000,
        backgroundColor: isDarkMode ? '#ffb74d' : '#1a237e',
        color: isDarkMode ? '#1a237e' : '#fff',
        boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.25)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          backgroundColor: isDarkMode ? '#ffa726' : '#0d1b60',
          transform: 'scale(1.1) rotate(360deg)',
          boxShadow: isDarkMode
            ? '0px 8px 24px rgba(255, 183, 77, 0.4)'
            : '0px 8px 24px rgba(26, 35, 126, 0.4)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isDarkMode ? (
        <LightModeIcon
          sx={{ fontSize: '28px', transition: 'transform 0.4s' }}
        />
      ) : (
        <DarkModeIcon sx={{ fontSize: '28px', transition: 'transform 0.4s' }} />
      )}
    </IconButton>
  );
};

const HomePage = () => {
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pathname = useSelector(getLocation)?.pathname ?? '';

  const widgetPreferences: WidgetPreference[] | null = useSelector(
    (state: any) => state.home?.widgetPreferences
  );

  useLayoutEffect(() => {
    const scrollNode = scrollRef.current;
    if (!scrollNode) {
      return undefined;
    }

    const syncScrollportHeight = () => {
      scrollNode.style.setProperty(
        '--home-scrollport-height',
        `${scrollNode.clientHeight}px`
      );
    };

    syncScrollportHeight();
    const resizeObserver = new ResizeObserver(syncScrollportHeight);
    resizeObserver.observe(scrollNode);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    document.title = 'Home - Aula';
    if (!widgetPreferences) {
      dispatch(fetchWidgetPreferences() as any);
    }
  }, [dispatch, widgetPreferences]);

  useEffect(() => {
    if (pathname.includes('/home')) {
      dispatch(applyPendingHomeSpaceReorder());
    }
  }, [dispatch, pathname]);

  return (
    <Box
      ref={scrollRef}
      display="flex"
      flexDirection="column"
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        // Keep vertical scroll contained; allow horizontal 2-finger browser
        // back/forward everywhere except the space carousel (handled there).
        overscrollBehaviorY: 'contain',
        overflowAnchor: 'none',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      <HomeWidgetLayout widgetPreferences={widgetPreferences} />
      <ThemeToggleFAB />
    </Box>
  );
};

export default HomePage;
